import { Locales } from '@griply/types';
import { ErrorLevel } from '@griply/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useCachedPushNotificationTokenQuery } from 'shared/hooks/queries/use-cached-push-notification-token-query';
import { useCreatePushTokenMutation } from 'shared/hooks/queries/use-create-push-token-mutation';
import { useFcmTokenDocQuery } from 'shared/hooks/queries/use-fcm-token-doc-query';
import { useAppForeground } from 'shared/hooks/use-app-foreground';
import {
  deletePushToken,
  fetchFreshPushToken,
} from 'shared/services/backend-api';
import { getErrorReporter } from 'shared/services/error-tracking';
import { PushRegistrationError } from 'shared/types/electron';
import { ID } from 'shared/types/id';
import { getIsElectronTag } from 'shared/utils/get-is-electron-tag';

const refreshIntervalMs = 24 * 60 * 60 * 1000;
// a failing FCM call must not re-fire on every focus — brief backoff instead of the old 24h lockout
const failedSyncBackoffMs = 5 * 60 * 1000;

// browser fetch network-failure messages: chromium / firefox / safari
const networkFailurePhrases = [
  'Failed to fetch',
  'NetworkError when attempting',
  'Load failed',
];

// heuristic, not a diagnosis: offline at catch time classifies ANY error as network-caused
const getIsLikelyNetworkFailure = (error: unknown): boolean => {
  if (!window.navigator.onLine) {
    return true;
  }
  const message = error instanceof Error ? error.message : '';
  return networkFailurePhrases.some((phrase) => message.includes(phrase));
};

const getFailureReportKey = (
  error: unknown,
  isLikelyNetworkFailure: boolean,
): string => {
  const code =
    error instanceof PushRegistrationError
      ? error.code
      : (error as { code?: string }).code ??
        (error instanceof Error ? error.message : String(error));
  // a network-interrupted failure must not suppress a later genuine failure with the same code
  return isLikelyNetworkFailure ? `network:${code}` : code;
};

export const usePushTokenSync = (
  uid: ID | undefined,
  locale: Locales,
): void => {
  const isSyncing = useRef(false);
  const lastRefreshAt = useRef(0);
  const lastFailedSyncAt = useRef(0);
  const reportedFailureKeys = useRef(new Set<string>());
  const { mutateAsync: createToken } = useCreatePushTokenMutation();

  const { data: token } = useCachedPushNotificationTokenQuery(uid);
  const { data: tokenDoc } = useFcmTokenDocQuery(token);

  const tokenRef = useRef(token);
  const tokenDocRef = useRef(tokenDoc);
  tokenRef.current = token;
  tokenDocRef.current = tokenDoc;

  const sync = useCallback(async () => {
    if (!uid) {
      return;
    }

    // the doc was likely deleted because the token went stale — re-register a fresh one
    const recoverMissingRegistration = async () => {
      const freshToken = await fetchFreshPushToken();
      if (!freshToken) {
        return;
      }
      await createToken({ userId: uid, token: freshToken, locale });
    };

    // the SDK rotates tokens without any callback — re-register on drift, otherwise rewrite so metadata/updatedAt stay current
    const refreshRegistration = async (currentToken: string) => {
      const liveToken = await fetchFreshPushToken();
      if (liveToken && liveToken !== currentToken) {
        getErrorReporter().captureMessage(
          'Registered push token no longer matches the live token — re-registered',
          ErrorLevel.Info,
          {
            tags: {
              source: 'push-token-sync',
              is_electron: getIsElectronTag(),
            },
          },
        );
        await createToken({ userId: uid, token: liveToken, locale });
        await deletePushToken(currentToken);
        return;
      }
      await createToken({ userId: uid, token: currentToken, locale });
    };

    if (isSyncing.current) {
      return;
    }
    isSyncing.current = true;

    try {
      const currentToken = tokenRef.current;
      if (!currentToken) {
        return;
      }

      const hasPermission =
        'Notification' in window && Notification.permission === 'granted';
      if (!hasPermission) {
        return;
      }

      if (Date.now() - lastFailedSyncAt.current <= failedSyncBackoffMs) {
        return;
      }

      if (!tokenDocRef.current) {
        await recoverMissingRegistration();
        return;
      }

      if (Date.now() - lastRefreshAt.current <= refreshIntervalMs) {
        return;
      }

      await refreshRegistration(currentToken);
      // stamped only on success — a failed refresh retries on the next focus
      lastRefreshAt.current = Date.now();
    } catch (error) {
      lastFailedSyncAt.current = Date.now();

      // failures repeat on every focus — report each distinct failure once per session
      const isLikelyNetworkFailure = getIsLikelyNetworkFailure(error);
      const reportKey = getFailureReportKey(error, isLikelyNetworkFailure);
      if (reportedFailureKeys.current.has(reportKey)) {
        return;
      }
      reportedFailureKeys.current.add(reportKey);

      getErrorReporter().captureException(error, {
        ...(isLikelyNetworkFailure && { level: ErrorLevel.Info }),
        tags: {
          source: 'push-token-sync',
          ...(isLikelyNetworkFailure && { network_failure: 'true' }),
          ...(error instanceof PushRegistrationError && {
            code: error.code,
            is_electron: getIsElectronTag(),
          }),
        },
      });
    } finally {
      isSyncing.current = false;
    }
  }, [uid, locale, createToken]);

  useAppForeground(() => {
    void sync();
  });

  useEffect(() => {
    void sync();
  }, [sync]);
};
